import { createContext, PropsWithChildren, useContext } from "react";
import { UrlObject } from "url";
import Link from "next/link";
import ChevronRight from "components/icons/24/actions/chevron-right.svg";
import { classes } from "lib/actions/util";

/**
 * Set when a crumb renders inside the "…" menu of folded crumbs rather than
 * in the trail itself. Levels after the first in the menu lead with a chevron,
 * echoing the trail's own chevrons.
 */
export const BreadcrumbMenuContext = createContext<{
  isInMenu: boolean;
  hasLevelChevron: boolean;
}>({ isInMenu: false, hasLevelChevron: false });

export default function BreadcrumbLink(
  p: PropsWithChildren<{
    href: UrlObject;
  }>,
) {
  const { isInMenu, hasLevelChevron } = useContext(BreadcrumbMenuContext);

  if (isInMenu) {
    const menuItemContent = (
      <>
        {hasLevelChevron && (
          <ChevronRight className="w-4 shrink-0 text-gray-400" />
        )}
        {p.children}
      </>
    );
    const menuItemClassName =
      "flex items-center gap-2 whitespace-nowrap rounded-md px-2.5 py-2 text-primary";
    if (!p.href)
      return <div className={menuItemClassName}>{menuItemContent}</div>;
    return (
      <Link
        href={p.href}
        role="menuitem"
        className={classes(menuItemClassName, "hover:bg-gray-100")}
      >
        {menuItemContent}
      </Link>
    );
  }

  let component = (
    <div className="flex min-w-0 items-center justify-around gap-1 whitespace-nowrap p-1">
      {p.children}
    </div>
  );
  if (p.href)
    component = (
      <Link
        href={p.href}
        className="block min-w-0 cursor-pointer rounded-md hover:bg-gray-50"
      >
        {component}
      </Link>
    );
  return component;
}
